import { execute } from "./executor";
import { interpret } from "./interpreter";
import { createBuffer } from "./model/buffer";
import type { CommandSet } from "./model/command-set";
import { clampCursor } from "./model/cursor";
import type { EditorState, SearchDirection } from "./model/editor-state";
import { pushUndoSnapshot } from "./model/history";
import type { Keystroke } from "./model/keystroke";
import { getMacro, withMacro } from "./model/macros";
import { EMPTY_PENDING } from "./model/pending-command";
import { moveLeft } from "./motions/basic";
import { findNextMatch } from "./search";
import { handleVisualKeystroke } from "./visual";

/** マクロ再生・ドットリピート再生の暴走防止用ステップ上限（design.md「マクロ」節）。 */
const MACRO_STEP_BUDGET = 10_000;
const MACRO_RUNAWAY_MESSAGE = "マクロが長すぎるため中断しました";

interface StepBudget {
  remaining: number;
}

/**
 * 1打の処理結果。`.` やマクロ再生（`@`）は「展開すべきキー列」を返し、
 * 呼び出し側（`runKeystroke`）が明示的なスタックで反復処理する。
 * これにより、自己参照マクロ等で再帰呼び出しが深くなってもネイティブの
 * 呼び出しスタックを消費せず、共有の `StepBudget` だけで安全に打ち切れる。
 */
interface StepOutcome {
  readonly state: EditorState;
  readonly expand?: readonly Keystroke[];
  readonly repeats?: number;
}

interface Frame {
  readonly keys: readonly Keystroke[];
  index: number;
  repeatsRemaining: number;
  readonly baseUsedKeystrokes: number;
  /**
   * true: `.`/`@`展開によるフレーム。完了時に必ず baseUsedKeystrokes+1 へ強制する
   * （内部で何キー分再生してもトリガー自体の1打分としてのみ数える）。
   * false: 最上位キーストローク自身のフレーム。usedKeystrokesは各処理が自然に設定した値をそのまま使う
   * （未習得キー等の0加算を上書きしない）。
   */
  readonly forceCountOnComplete: boolean;
}

function isInsertableKeystroke(keystroke: Keystroke): boolean {
  if (keystroke.ctrl) {
    return false;
  }
  if (keystroke.key === "<CR>" || keystroke.key === "<BS>" || keystroke.key === "<Del>") {
    return true;
  }
  return keystroke.key.length === 1;
}

function insertKeystroke(state: EditorState, keystroke: Keystroke): EditorState {
  if (keystroke.ctrl) {
    return state;
  }

  const { buffer, cursor } = state;
  const line = buffer.lines[cursor.line] ?? "";

  if (keystroke.key === "<CR>") {
    const before = line.slice(0, cursor.col);
    const after = line.slice(cursor.col);
    const lines = [
      ...buffer.lines.slice(0, cursor.line),
      before,
      after,
      ...buffer.lines.slice(cursor.line + 1),
    ];
    return { ...state, buffer: createBuffer(lines), cursor: { line: cursor.line + 1, col: 0 } };
  }

  if (keystroke.key === "<BS>") {
    if (cursor.col > 0) {
      const text = line.slice(0, cursor.col - 1) + line.slice(cursor.col);
      const lines = [...buffer.lines];
      lines[cursor.line] = text;
      return {
        ...state,
        buffer: createBuffer(lines),
        cursor: { line: cursor.line, col: cursor.col - 1 },
      };
    }
    if (cursor.line > 0) {
      const prev = buffer.lines[cursor.line - 1] ?? "";
      const lines = [
        ...buffer.lines.slice(0, cursor.line - 1),
        prev + line,
        ...buffer.lines.slice(cursor.line + 1),
      ];
      return {
        ...state,
        buffer: createBuffer(lines),
        cursor: { line: cursor.line - 1, col: prev.length },
      };
    }
    return state;
  }

  if (keystroke.key === "<Del>") {
    if (cursor.col < line.length) {
      const text = line.slice(0, cursor.col) + line.slice(cursor.col + 1);
      const lines = [...buffer.lines];
      lines[cursor.line] = text;
      return { ...state, buffer: createBuffer(lines), cursor };
    }
    if (cursor.line < buffer.lines.length - 1) {
      const nextLine = buffer.lines[cursor.line + 1] ?? "";
      const lines = [
        ...buffer.lines.slice(0, cursor.line),
        line + nextLine,
        ...buffer.lines.slice(cursor.line + 2),
      ];
      return { ...state, buffer: createBuffer(lines), cursor };
    }
    return state;
  }

  if (keystroke.key.length !== 1) {
    return state;
  }

  const text = line.slice(0, cursor.col) + keystroke.key + line.slice(cursor.col);
  const lines = [...buffer.lines];
  lines[cursor.line] = text;
  return {
    ...state,
    buffer: createBuffer(lines),
    cursor: { line: cursor.line, col: cursor.col + 1 },
  };
}

function substituteInLine(
  line: string,
  pattern: string,
  replacement: string,
  all: boolean,
): string {
  if (pattern === "") {
    return line;
  }
  if (!all) {
    const index = line.indexOf(pattern);
    if (index === -1) {
      return line;
    }
    return line.slice(0, index) + replacement + line.slice(index + pattern.length);
  }
  return line.split(pattern).join(replacement);
}

interface ParsedSubstitute {
  readonly wholeBuffer: boolean;
  readonly pattern: string;
  readonly replacement: string;
  readonly all: boolean;
}

/** `:s/old/new/` `:%s/old/new/g` を解析する（正規表現は使わずリテラル一致のみ扱う）。 */
function parseSubstitute(command: string): ParsedSubstitute | null {
  const wholeBuffer = command.startsWith("%");
  const body = wholeBuffer ? command.slice(1) : command;
  if (!body.startsWith("s")) {
    return null;
  }
  const rest = body.slice(1);
  if (rest.length === 0) {
    return null;
  }
  const delimiter = rest[0] as string;
  const parts = rest.slice(1).split(delimiter);
  if (parts.length < 2) {
    return null;
  }
  const [pattern = "", replacement = "", flags = ""] = parts;
  return { wholeBuffer, pattern, replacement, all: flags.includes("g") };
}

/** `:` コマンドライン（`:s` `:%s` `:w` `:q`）を確定する。 */
function confirmExCommand(state: EditorState, command: string): EditorState {
  const base = { ...state, mode: "normal" as const, commandLine: null };

  if (command === "") {
    return { ...base, statusMessage: null };
  }
  if (command === "w") {
    return { ...base, statusMessage: "保存しました" };
  }
  if (command === "q") {
    return { ...base, statusMessage: "終了コマンドを実行しました" };
  }

  const parsed = parseSubstitute(command);
  if (!parsed) {
    return { ...base, statusMessage: `不明なコマンドです: :${command}` };
  }
  const { wholeBuffer, pattern, replacement, all } = parsed;
  if (pattern === "") {
    return { ...base, statusMessage: null };
  }

  const targetLine = state.cursor.line;
  let changed = false;
  const lines = state.buffer.lines.map((line, index) => {
    if (!wholeBuffer && index !== targetLine) {
      return line;
    }
    const next = substituteInLine(line, pattern, replacement, all);
    if (next !== line) {
      changed = true;
    }
    return next;
  });

  if (!changed) {
    return { ...base, statusMessage: `パターンが見つかりません: ${pattern}` };
  }

  const history = pushUndoSnapshot(state.history, { buffer: state.buffer, cursor: state.cursor });
  const buffer = createBuffer(lines);
  const cursor = clampCursor(buffer, state.cursor);
  return { ...base, buffer, cursor, history, statusMessage: null };
}

/** コマンドラインモード（`/pattern<CR>` `?pattern<CR>` `:command<CR>`）を確定する。 */
function confirmCommandLine(state: EditorState): EditorState {
  const commandLine = state.commandLine ?? "";
  const prefix = commandLine.charAt(0);

  if (prefix === ":") {
    return confirmExCommand(state, commandLine.slice(1));
  }

  const typed = commandLine.slice(1);
  const direction: SearchDirection = prefix === "?" ? "backward" : "forward";
  const query = typed !== "" ? typed : (state.lastSearch?.query ?? "");
  const base = { ...state, mode: "normal" as const, commandLine: null };

  if (query === "") {
    return { ...base, statusMessage: null };
  }

  const match = findNextMatch(state.buffer, state.cursor, query, direction);
  const lastSearch = { query, direction };
  if (!match) {
    return { ...base, lastSearch, statusMessage: `パターンが見つかりません: ${query}` };
  }
  return { ...base, cursor: clampCursor(state.buffer, match), lastSearch, statusMessage: null };
}

/**
 * 1打分の処理本体。`.`（ドットリピート）と `@`（マクロ再生）は、自ら再帰的に
 * 展開キー列を処理するのではなく `expand`（+`repeats`）として返す。実際の反復は
 * 呼び出し元の `runKeystroke` が明示的なスタックで行う（再帰スタック枯渇を避けるため）。
 */
function applyOneStep(
  state: EditorState,
  keystroke: Keystroke,
  commandSet: CommandSet,
): StepOutcome {
  // `Esc` は全モードから無条件にノーマルモードへ戻す（保留中コマンドの破棄を含む）。
  if (keystroke.key === "<Esc>") {
    const lastChange =
      state.mode === "insert" && state.insertSession
        ? [...state.insertSession, keystroke]
        : state.lastChange;
    return {
      state: {
        ...state,
        mode: "normal",
        cursor: clampCursor(state.buffer, state.cursor),
        pending: EMPTY_PENDING,
        insertSession: null,
        commandLine: null,
        visualAnchor: null,
        lastChange,
        statusMessage: null,
        usedKeystrokes: state.usedKeystrokes + 1,
      },
    };
  }

  if (state.mode === "insert") {
    if (!isInsertableKeystroke(keystroke)) {
      return { state };
    }
    const applied = insertKeystroke(state, keystroke);
    return {
      state: {
        ...applied,
        insertSession: state.insertSession ? [...state.insertSession, keystroke] : [keystroke],
        statusMessage: null,
        usedKeystrokes: state.usedKeystrokes + 1,
      },
    };
  }

  if (state.mode === "command-line") {
    if (keystroke.key === "<CR>") {
      return { state: { ...confirmCommandLine(state), usedKeystrokes: state.usedKeystrokes + 1 } };
    }
    if (keystroke.key === "<BS>") {
      const commandLine = state.commandLine ?? "";
      if (commandLine.length <= 1) {
        return {
          state: {
            ...state,
            mode: "normal",
            commandLine: null,
            statusMessage: null,
            usedKeystrokes: state.usedKeystrokes + 1,
          },
        };
      }
      return {
        state: {
          ...state,
          commandLine: commandLine.slice(0, -1),
          usedKeystrokes: state.usedKeystrokes + 1,
        },
      };
    }
    if (keystroke.ctrl || keystroke.key.length !== 1) {
      return { state };
    }
    return {
      state: {
        ...state,
        commandLine: (state.commandLine ?? "") + keystroke.key,
        usedKeystrokes: state.usedKeystrokes + 1,
      },
    };
  }

  if (state.mode === "visual" || state.mode === "visual-line" || state.mode === "visual-block") {
    const next = handleVisualKeystroke(state, keystroke, commandSet);
    return { state: { ...next, usedKeystrokes: state.usedKeystrokes + 1 } };
  }

  if (keystroke.key === "<BS>") {
    const range = moveLeft(state.buffer, state.cursor, null);
    return {
      state: {
        ...state,
        cursor: clampCursor(state.buffer, range.end),
        pending: EMPTY_PENDING,
        statusMessage: null,
        usedKeystrokes: state.usedKeystrokes + 1,
      },
    };
  }

  if (keystroke.key === "<Del>") {
    const executed = execute(state, { kind: "simple-edit", name: "x", count: null });
    const lastChange = executed.buffer !== state.buffer ? [keystroke] : state.lastChange;
    const statusMessage =
      executed.statusMessage !== state.statusMessage ? executed.statusMessage : null;
    return {
      state: {
        ...executed,
        pending: EMPTY_PENDING,
        lastChange,
        statusMessage,
        usedKeystrokes: state.usedKeystrokes + 1,
      },
    };
  }

  // 記録中に素の `q`（保留中コマンドなし）が押されたら、他の解釈より先に記録を停止する。
  if (
    state.macroRecording !== null &&
    keystroke.key === "q" &&
    !keystroke.ctrl &&
    state.pending.awaiting === null &&
    state.pending.operator === null
  ) {
    const macros = withMacro(state.macros, state.macroRecording.name, state.macroRecording.keys);
    return {
      state: {
        ...state,
        macros,
        macroRecording: null,
        pending: EMPTY_PENDING,
        statusMessage: null,
        usedKeystrokes: state.usedKeystrokes + 1,
      },
    };
  }

  const result = interpret(state.pending, keystroke, commandSet);

  if (result.type === "unlearned") {
    return { state: { ...state, pending: EMPTY_PENDING, statusMessage: result.statusMessage } };
  }

  if (result.type === "pending") {
    return {
      state: {
        ...state,
        pending: result.pending,
        statusMessage: null,
        usedKeystrokes: state.usedKeystrokes + 1,
      },
    };
  }

  if (result.command.kind === "repeat") {
    if (state.lastChange === null) {
      return {
        state: {
          ...state,
          pending: EMPTY_PENDING,
          statusMessage: "直前の変更がありません",
          usedKeystrokes: state.usedKeystrokes + 1,
        },
      };
    }
    return { state: { ...state, pending: EMPTY_PENDING }, expand: state.lastChange, repeats: 1 };
  }

  if (result.command.kind === "play-macro") {
    const { name, count } = result.command;
    const keys = getMacro(state.macros, name);
    if (!keys || keys.length === 0) {
      return {
        state: {
          ...state,
          pending: EMPTY_PENDING,
          statusMessage: `マクロが記録されていません: ${name}`,
          usedKeystrokes: state.usedKeystrokes + 1,
        },
      };
    }
    const times = Math.max(count ?? 1, 1);
    return { state: { ...state, pending: EMPTY_PENDING }, expand: keys, repeats: times };
  }

  const executed = execute(state, result.command);
  const lastChange = executed.buffer !== state.buffer ? result.rawKeys : state.lastChange;
  // executeが明示的に新しいstatusMessageを設定した場合のみそれを使い、それ以外は成功として消去する。
  const statusMessage =
    executed.statusMessage !== state.statusMessage ? executed.statusMessage : null;
  // 挿入モードへ入るコマンド自身も、そのキーからinsertSessionの記録を開始する
  // （でないと後続の文字だけが記録され、ドットリピート再生時に挿入モード開始キーが欠落する）。
  const insertSession =
    result.command.kind === "enter-insert" ? [keystroke] : executed.insertSession;

  return {
    state: {
      ...executed,
      pending: EMPTY_PENDING,
      lastChange,
      insertSession,
      statusMessage,
      usedKeystrokes: state.usedKeystrokes + 1,
    },
  };
}

/**
 * 1打のキーストロークを処理し、展開（`.` / `@`）が生じた場合は明示的なスタックで
 * 反復する。共有の `StepBudget` を全フレームで消費することで、ネストしたマクロや
 * 自己参照マクロでもネイティブ再帰に頼らず安全に暴走を打ち切れる。
 */
function runKeystroke(
  initial: EditorState,
  keystroke: Keystroke,
  commandSet: CommandSet,
): EditorState {
  const budget: StepBudget = { remaining: MACRO_STEP_BUDGET };
  let current = initial;
  const stack: Frame[] = [
    {
      keys: [keystroke],
      index: 0,
      repeatsRemaining: 1,
      baseUsedKeystrokes: initial.usedKeystrokes,
      forceCountOnComplete: false,
    },
  ];

  while (stack.length > 0) {
    const frame = stack[stack.length - 1] as Frame;

    if (frame.index >= frame.keys.length) {
      if (frame.repeatsRemaining > 1) {
        frame.repeatsRemaining -= 1;
        frame.index = 0;
        continue;
      }
      stack.pop();
      if (frame.forceCountOnComplete) {
        current = { ...current, usedKeystrokes: frame.baseUsedKeystrokes + 1 };
      }
      continue;
    }

    if (budget.remaining <= 0) {
      current = { ...current, statusMessage: MACRO_RUNAWAY_MESSAGE };
      while (stack.length > 0) {
        const finishing = stack.pop() as Frame;
        if (finishing.forceCountOnComplete) {
          current = { ...current, usedKeystrokes: finishing.baseUsedKeystrokes + 1 };
        }
      }
      break;
    }

    const key = frame.keys[frame.index] as Keystroke;
    frame.index += 1;
    budget.remaining -= 1;

    const outcome = applyOneStep(current, key, commandSet);
    current = outcome.state;

    if (outcome.expand && outcome.expand.length > 0) {
      stack.push({
        keys: outcome.expand,
        index: 0,
        repeatsRemaining: outcome.repeats ?? 1,
        baseUsedKeystrokes: current.usedKeystrokes,
        forceCountOnComplete: true,
      });
    }
  }

  return current;
}

/**
 * vimエンジンの公開API。正規化済みの1打（Keystroke）を受け取り、新しいEditorStateを返す純関数。
 * I/O・副作用は一切持たない（NFR-002の担保）。
 *
 * マクロ記録中は、この1打分のキーストロークを（記録開始・終了キー自体を除いて）
 * `macroRecording.keys` へ追記する。ドットリピート・マクロ再生が内部的に展開する
 * キーストロークは記録対象に含めない（本家vimと同様、`.` や `@a` の呼び出し自体を記録する）。
 */
export function reduce(
  state: EditorState,
  keystroke: Keystroke,
  commandSet: CommandSet,
): EditorState {
  const wasRecording = state.macroRecording;
  const result = runKeystroke(state, keystroke, commandSet);

  if (wasRecording !== null && result.macroRecording !== null) {
    return {
      ...result,
      macroRecording: {
        ...result.macroRecording,
        keys: [...result.macroRecording.keys, keystroke],
      },
    };
  }
  return result;
}
